/**
 * Pure presentation component for the Save button section rendered at
 * the top of the extraction review view.
 *
 * Renders the primary Save button plus the confirmation dialog that
 * appears when the user clicks Save with unresolved conflicts still
 * present. The Save button is hidden entirely when the extraction is
 * already `SAVED` (read-only mode) — the Container decides whether to
 * mount this view at all.
 * @module features/contracts/components/SaveExtractionSection/SaveExtractionSectionView
 */

import { Check } from "lucide-react-native";
import React, { memo } from "react";

import SaveExtractionDialog from "@/features/contracts/components/molecules/SaveExtractionDialog";

import { Button } from "@/components/atoms/Button";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for {@link SaveExtractionSectionView}. */
interface Props {
  /** Whether a save mutation is currently in flight. */
  readonly isSaving: boolean;
  /** Whether the confirm-unresolved dialog is currently visible. */
  readonly isConfirmDialogOpen: boolean;
  /**
   * Display-formatted names of categories that still have unresolved
   * conflicts at the time the Save button was clicked. Surfaced in the
   * confirmation dialog body.
   */
  readonly unresolvedCategoryNames: readonly string[];
  /**
   * Last error message from a failed save attempt, or null when no
   * error is currently displayed. Cleared on the next successful save.
   * Suppressed in compact mode — the container surfaces errors via toast
   * instead so the inline button can sit on a single-row action strip.
   */
  readonly errorMessage: string | null;
  /** Invoked when the user taps the primary Save button. */
  readonly onSavePress: () => void;
  /** Invoked when the user cancels the confirmation dialog. */
  readonly onCancelConfirm: () => void;
  /** Invoked when the user confirms saving with unresolved conflicts. */
  readonly onConfirmSave: () => void;
  /**
   * When true, render only the Save button + dialog (no outer VStack /
   * HStack wrapping, no inline error text). Used by the drawer body
   * (SE-4767) to slot the Save button into the StatusStrip's action row
   * alongside Edit and Discard. Errors surface via toast in compact
   * mode so the action row stays a single horizontal line.
   */
  readonly compact?: boolean;
}

/**
 * Renders the Save button and save-confirmation dialog.
 * @param props - View props
 * @param props.isSaving - Whether the save mutation is in flight
 * @param props.isConfirmDialogOpen - Whether the confirmation dialog is visible
 * @param props.unresolvedCategoryNames - Category names with unresolved conflicts
 * @param props.errorMessage - Error message to display under the button, or null
 * @param props.onSavePress - Invoked when the Save button is tapped
 * @param props.onCancelConfirm - Invoked when the user cancels the dialog
 * @param props.onConfirmSave - Invoked when the user confirms the save
 * @param props.compact - When true, render only the button + dialog with
 *   no outer wrapping or inline error text; intended for slotting into
 *   the StatusStrip action row (SE-4767)
 * @returns The rendered section
 */
/**
 * The Save button.
 * @param props - View props.
 * @returns The button.
 */
const renderSaveButton = (props: Props): React.ReactElement => (
  <Button
    testID="save-extraction-btn"
    label={props.isSaving ? "Saving..." : "Save"}
    tone="primary"
    size="sm"
    icon={Check}
    onPress={props.onSavePress}
    isDisabled={props.isSaving}
    isLoading={props.isSaving}
    className={(props.compact ?? false) ? undefined : "self-start"}
  />
);

/**
 * The unresolved-categories confirmation.
 * @param props - View props.
 * @returns The dialog.
 */
const renderDialog = (props: Props): React.ReactElement => (
  <SaveExtractionDialog
    isOpen={props.isConfirmDialogOpen}
    isSaving={props.isSaving}
    unresolvedCategoryNames={props.unresolvedCategoryNames}
    onClose={props.onCancelConfirm}
    onConfirm={props.onConfirmSave}
  />
);

/**
 * Renders the save affordance and its confirmation.
 *
 * The compact form is the drawer header's: the button alone, no surrounding
 * Stack and no inline error line, because the header has nowhere to put one.
 * @param props - View props.
 * @returns The section.
 */
const SaveExtractionSectionView = (props: Props) =>
  props.compact === true ? (
    <>
      {renderSaveButton(props)}
      {renderDialog(props)}
    </>
  ) : (
    <Stack className="gap-2">
      <Stack direction="horizontal" className="items-center justify-between">
        {renderSaveButton(props)}
      </Stack>
      {props.errorMessage !== null ? (
        <Box testID="save-extraction-error">
          <Text variant="caption" className="text-needs-semantic-error-600">
            {props.errorMessage}
          </Text>
        </Box>
      ) : null}
      {renderDialog(props)}
    </Stack>
  );

SaveExtractionSectionView.displayName = "SaveExtractionSectionView";

export default memo(SaveExtractionSectionView);
